import { useLayoutEffect, useRef, useState } from 'react';

interface IOverflowFilterChipItem {
  key: string;
  label: string;
  onRemove?: () => void;
}

interface IUseOverflowFilterChips {
  items: IOverflowFilterChipItem[];
}

export const useOverflowFilterChips = ({ items }: IUseOverflowFilterChips) => {
  const [visibleItemCount, setVisibleItemCount] = useState(0);
  const listRef = useRef<HTMLDivElement | null>(null);
  const measureRef = useRef<HTMLDivElement | null>(null);

  const hiddenItemCount = items.length - visibleItemCount;
  const overflowLabel = `+${hiddenItemCount}`;

  useLayoutEffect(() => {
    const measureVisibleItems = () => {
      const listElement = listRef.current;
      const measureElement = measureRef.current;

      if (!listElement || !measureElement) {
        return;
      }

      const availableWidth = listElement.clientWidth;

      if (!availableWidth) {
        setVisibleItemCount(items.length);

        return;
      }

      const overflowWidth =
        measureElement.querySelector<HTMLElement>('[data-overflow-chip]')
          ?.offsetWidth ?? 0;
      const chipElements = Array.from(
        measureElement.querySelectorAll<HTMLElement>('[data-chip-width]'),
      );
      const gap = 8;

      let usedWidth = 0;
      let nextVisibleCount = chipElements.length;

      for (let index = 0; index < chipElements.length; index += 1) {
        const chipWidth = chipElements[index].offsetWidth;
        const nextUsedWidth = usedWidth + (index > 0 ? gap : 0) + chipWidth;
        const hasRemainingItems = index < chipElements.length - 1;
        const reservedOverflowWidth = hasRemainingItems
          ? gap + overflowWidth
          : 0;

        if (nextUsedWidth + reservedOverflowWidth > availableWidth) {
          nextVisibleCount = index;
          break;
        }

        usedWidth = nextUsedWidth;
      }

      setVisibleItemCount(nextVisibleCount);
    };

    measureVisibleItems();

    const resizeObserver = new ResizeObserver(() => {
      measureVisibleItems();
    });

    if (listRef.current) {
      resizeObserver.observe(listRef.current);
    }

    if (measureRef.current) {
      resizeObserver.observe(measureRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [items, overflowLabel]);

  return {
    hiddenItemCount,
    listRef,
    measureRef,
    overflowLabel,
    visibleItems: items.slice(0, visibleItemCount),
  };
};

export type { IOverflowFilterChipItem };
